import { useEffect, useLayoutEffect, useRef } from 'react'
import './App.css'
import { useUserSelector } from './store/selectors/userSelector'
import { useUserApi } from './store/api/hooks/userApiHook'
import { useFpLoader } from './store/api/hooks/fpLoaderHook'
import { MsalAuthenticationTemplate, useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react'
import { loginRequest } from './_config/authConfig'
import { InteractionRequiredAuthError, InteractionType } from '@azure/msal-browser'
import Auth from './components/Auth'
import { Router } from './router'
import './utils/analytics/datadog'
import i18n, { APP_LANGUAGE_STORAGE_KEY, normalizeLanguage } from './i18n'
import { useLanguageSelector } from './store/selectors/fpLoaderSelector'
import { useLanguage } from './store/api/hooks/langHook'
import { useTranslation } from 'react-i18next'
import { setRumUser, trackAction, trackError } from './utils/analytics'
import { KEYS } from './_config'
import pusherService from './services/pusherService'
// import NpsWidget from './components/NpsWidget'
import { ConfirmDialogProvider } from './components/confirmDialog'
import { NpsProvider } from './features/nps'

function App() {
    const { t } = useTranslation()
    const userAPI: any = useUserApi()
    const user: any = useUserSelector()
    const { hideLoader } = useFpLoader()
    const lang = useLanguageSelector()
    const { setLanguage } = useLanguage()
    const { instance, accounts } = useMsal()
    const account: any = useAccount(accounts[0] || {})
    const isAuthenticated = useIsAuthenticated()

    const AUTH: any = useRef()

    const initializeMSAL = async () => {
        try {
            await instance.initialize()
            ;(globalThis as typeof globalThis & Window).MSALINSTANCE = instance
            ;(globalThis as typeof globalThis & Window).MSALACCOUNT = account
        } catch (error) {
            console.error('Error during MSAL initialization:', error)
        }
    }

    const errorComponent = () => {
        return <p>{t('Unauthorized')}</p>
    }

    const loginHandler = (data: any) => {
        console.log('Login Handler:', data)
    }

    const getBusinessOwnerDetails = () => {
        if (user) {
            userAPI.getBusinessOwnerDetails(
                { literal: user?.user_literal },
                (resp: any) => {},
                (err: any) => {
                    console.error('Error fetching business owner details:', err)
                },
            )
        }
    }

    useEffect(() => {
        if (isAuthenticated && accounts.length > 0) {
            const user = accounts[0]
            if (user) {
                setRumUser({
                    id: user.homeAccountId,
                    email: user.username,
                })
            }
        }
    }, [isAuthenticated, accounts])

    useEffect(() => {
        if (account) {
            initializeMSAL()
                .then(() =>
                    instance.acquireTokenSilent({
                        scopes: loginRequest.scopes,
                        prompt: 'consent',
                        account,
                    }),
                )
                .then((response: any) => {
                    localStorage.setItem(KEYS.AUTH_ACCESS_TOKEN, response.accessToken)
                    if (location.pathname == '/') {
                        AUTH?.current?.login('login')
                    } else {
                        getBusinessOwnerDetails()
                    }
                })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        void instance.acquireTokenRedirect(loginRequest)
                    } else {
                        console.error('Error :', error)
                    }
                })
        }
    }, [account, instance])

    useEffect(() => {
        if (user?.user_literal || user?.email) {
            setRumUser({
                id: user?.user_literal || user?.email || 'unknown_user',
                email: user?.email,
                name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || undefined,
                zones: user?.zones,
                business_functions: user?.business_functions,
                countries: user?.countries,
                method: 'SSO',
            })

            trackAction('app_opened', {
                timestamp: new Date().toISOString(),
                email: user?.email,
                user_id: user?.user_literal,
                first_name: user?.first_name,
                last_name: user?.last_name,
                zones: user?.zones,
                business_functions: user?.business_functions,
                countries: user?.countries,
            })
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user])

    useEffect(() => {
        if (user?.user_literal) {
            localStorage.setItem(KEYS.USER, user?.user_literal)
            getBusinessOwnerDetails()
        }
    }, [user])
    useLayoutEffect(() => {
        hideLoader()

        const handleChunkError = (e: any) => {
            if (e?.message?.includes('Loading chunk')) {
                trackError(new Error('Chunk load failed'), { message: e?.message })
                globalThis.location.reload()
            }
        }

        const handleUnhandledRejection = (e: any) => trackError(e.reason instanceof Error ? e.reason : new Error(String(e.reason)), { source: 'unhandled_rejection' })

        globalThis.addEventListener('error', handleChunkError)
        globalThis.addEventListener('unhandledrejection', handleUnhandledRejection)

        return () => {
            globalThis.removeEventListener('error', handleChunkError)
            globalThis.removeEventListener('unhandledrejection', handleUnhandledRejection)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (lang && i18n.language !== lang) {
            void i18n.changeLanguage(lang)
        }
    }, [lang])

    useEffect(() => {
        const syncLanguageFromStorage = (event: StorageEvent) => {
            if (event.key !== APP_LANGUAGE_STORAGE_KEY) return
            const nextLanguage = normalizeLanguage(event.newValue)

            if (nextLanguage !== lang) {
                setLanguage(nextLanguage)
            }
        }

        globalThis.addEventListener('storage', syncLanguageFromStorage)
        return () => globalThis.removeEventListener('storage', syncLanguageFromStorage)
    }, [lang, setLanguage])

    useEffect(() => {
        if (user?.user_literal) {
            pusherService.connect(user.user_literal)
        }

        return () => {
            pusherService.disconnect()
        }
    }, [user])

    return (
        <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            authenticationRequest={{
                scopes: loginRequest.scopes,
                prompt: 'select_account',
            }}
            errorComponent={errorComponent}
        >
            <ConfirmDialogProvider>
                <NpsProvider>
                    <Auth _ref={AUTH} onLogin={loginHandler} />
                    {isAuthenticated && (
                        <>
                            <Router />
                            {/* <NpsWidget /> */}
                        </>
                    )}
                </NpsProvider>
            </ConfirmDialogProvider>
        </MsalAuthenticationTemplate>
    )
}

export default App
